import { useCallback, useEffect, useRef, useState } from "react";

import { ObjectDocumentObjectType, ObjectDocumentSource } from "../../constants";
import { UploadedDocument } from "../../models";
import { downloadDealDocument } from "../../service/fund";
import { createObjectDocument, deleteObjectDocument } from "../../service/interaction";
import { downloadProjectDocument } from "../../service/project";
import { getObjectDocuments, searchDealDocuments, searchProjectDocuments } from "../../service/query";
import { ServiceError, Status } from "../../service/Shared";
import { Toast } from "../../widget";
import { initiateDocumentDownloadViaBrowser } from "../document";

// The document store an attachment lives in, which a download has to search to reach its latest version.
export type ObjectAttachmentScope = "project" | "group" | "deal";

// A document linked to the open object, or staged against an object that does not exist yet.
export interface ObjectAttachment {
  /*
    Identity and concurrency token of the object_document link. Both are null while the attachment is only
    staged — an unlink needs them, so a staged attachment is dropped locally instead.
  */
  uuid: string | null;
  rowVersion: number | null;
  documentUuid: string;
  documentSource: string;
  documentType: string | null;
  variant: string | null;
  fileName: string;
  url: string | null;
  uploadedAt: Date | null;
}

interface UseObjectAttachmentsProps {
  // Undefined while the object is being created — uploads are staged until saveAttachments is called.
  objectUuid: string | undefined;

  /*
    The object_document objectType. Deliberately not InteractionObjectType, which the tag and note endpoints
    use: object_document spells the transaction type "IssuanceTransaction" where a tag spells it
    "Issuance Transaction", and the server rejects the spaced form here.
  */
  objectType: ObjectDocumentObjectType;

  /*
    Fixed by the parent the documents live under, since the server only accepts a source matching that parent
    (BR-02811). Undefined for a parent that holds no documents — nothing can be linked, so the callers that
    gate on it never reach the link.
  */
  documentSource: ObjectDocumentSource | undefined;

  documentScope?: ObjectAttachmentScope;
  parentUuid?: string | null;

  /*
    Names the object in the failure messages, e.g. "task" gives "The task's attachments could not be loaded".
    Only ever reaches the user inside those messages.
  */
  subjectLabel?: string;
}

interface UseObjectAttachmentsReturnData {
  attachments: ObjectAttachment[];
  isAttachmentsLoading: boolean;

  /*
    Taken when an upload flow is started and handed back to handleDocumentsUploaded with the documents it
    produced. An upload can finish after the panel has closed or moved to another object, and only the caller
    knows when it began, so the panel's state at that moment cannot be recovered without this.
  */
  beginUpload: () => number;

  handleDocumentsUploaded: (documents: UploadedDocument[], uploadToken: number) => Promise<void>;
  handleRemoveAttachment: (documentUuid: string) => Promise<void>;

  /*
    Downloads an attachment through the authenticated endpoint its store exposes, as every other document
    table in the app does. The file url the link carries is not a substitute: a private document's file is
    not readable without a session, so opening the url downloads nothing.
  */
  handleDownloadAttachment: (documentUuid: string) => Promise<void>;

  /*
    Two-phase link, mirroring saveTags: called with the persisted uuid once a newly created object exists, so
    attachments staged during create can be linked. Returns errors, if any.
  */
  saveAttachments: (savedObjectUuid: string) => Promise<ServiceError[] | undefined>;

  /*
    Clears the list when the panel is closed. The panel stays mounted between openings, and an object being
    created keeps a null uuid throughout, so nothing else would drop attachments staged against the object
    that was just saved — they would show against, and be linked to, the next one created.
  */
  resetAttachments: () => void;
}

/*
  Documents linked to an object through object_document. Against an existing object a link is written as soon
  as a document is uploaded or removed; while the object is still being created there is nothing to link to,
  so uploads are staged and flushed by saveAttachments once it exists.
*/
export const useObjectAttachments = ({
  objectUuid,
  objectType,
  documentSource,
  documentScope,
  parentUuid,
  subjectLabel = "object",
}: UseObjectAttachmentsProps): UseObjectAttachmentsReturnData => {
  const [attachments, setAttachments] = useState<ObjectAttachment[]>([]);
  const [isAttachmentsLoading, setIsAttachmentsLoading] = useState(false);
  // Monotonic id of the latest load, so a response for an object the panel has moved on from is discarded.
  const requestIdRef = useRef(0);
  /*
    Identity of the object the panel currently holds, bumped when it opens another one or closes. A mutation
    started against an earlier object must not reload once that has happened: its reload would be the newest,
    so the earlier object's documents would be shown against the one now open — and removing one of those
    would unlink it from the earlier object.
  */
  const lifecycleRef = useRef(0);

  const fetchAttachments = useCallback(async () => {
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;

    // Create mode: nothing is linked yet, and any staged uploads belong to the panel that is open.
    if (!objectUuid) {
      setAttachments([]);
      return;
    }

    setIsAttachmentsLoading(true);

    const res = await getObjectDocuments({ objectUuid, objectType });

    /*
      Closing one object and opening another overlaps two loads; only the newest may apply its result, or the
      previous object's documents are shown against this one.
    */
    if (requestId !== requestIdRef.current) return;

    setIsAttachmentsLoading(false);

    if (res.status === Status.Success && res.data?.results != null) {
      setAttachments(
        res.data.results.map((document) => ({
          uuid: document.uuid,
          rowVersion: document.rowVersion,
          documentUuid: document.documentUuid,
          documentSource: document.documentSource,
          documentType: document.documentType,
          variant: document.variant,
          fileName: document.fileName,
          url: document.url,
          uploadedAt: document.uploadedAt,
        }))
      );
      return;
    }

    if (res.status === Status.Error) {
      Toast.error({ message: res.errors?.[0]?.message ?? `The ${subjectLabel}'s attachments could not be loaded` });
    }
  }, [objectUuid, objectType, subjectLabel]);

  useEffect(() => {
    lifecycleRef.current += 1;
    fetchAttachments();
  }, [fetchAttachments]);

  // Links one document to an object that already exists. Returns the error, if the link was rejected.
  const linkDocument = async (resolvedObjectUuid: string, documentUuid: string): Promise<ServiceError | undefined> => {
    // A parent that holds no documents has nothing to link against, and no control that could reach here.
    if (!documentSource) return undefined;

    const res = await createObjectDocument({
      objectUuid: resolvedObjectUuid,
      objectType,
      documentUuid,
      documentSource,
    });

    if (res.status !== Status.Error) return undefined;

    /*
      A rejected link reports errors: [] for anything the service could not read a message from — a network
      failure, or a response with no body — so the absence of one is a failure without detail, not a success.
      Returning undefined here would let the caller count it as linked and drop the attachment silently.
    */
    return res.errors?.[0] ?? { code: "OBJECT_DOCUMENT_LINK_FAILED", message: "The document could not be attached" };
  };

  const handleDocumentsUploaded = async (documents: UploadedDocument[], uploadToken: number): Promise<void> => {
    if (documents.length === 0 || !documentSource) return;

    /*
      An upload outlives the panel that started it: the file is still transferring while the user can close the
      object, or open another. The token says which panel session it was started from, and the object it was
      meant for is the one this handler closed over — neither can be read off the panel's state by the time it
      lands.

      Asked at each point state is about to be written, never up front: the panel can also move on part-way
      through this handler, while the links are being written.
    */
    const isCurrentPanelSession = (): boolean => uploadToken === lifecycleRef.current;

    /*
      Create mode: the object has no uuid to link against yet, so hold the uploads locally and show them from
      what the upload reported. saveAttachments links them once it has been created.
    */
    if (!objectUuid) {
      /*
        Staging is the only record of an upload made in create mode, and an object being created keeps a null
        uuid, so a document from a create session that has since been closed cannot be told apart from this
        one's — it would be shown against the next object created, and linked to it on save.
      */
      if (!isCurrentPanelSession()) {
        Toast.error({
          message: `The document was uploaded, but the ${subjectLabel} closed before it finished, so it was not attached`,
        });
        return;
      }

      setAttachments((prev) => [
        ...prev,
        ...documents
          .filter((document) => !prev.some((existing) => existing.documentUuid === document.uuid))
          .map((document) => ({
            uuid: null,
            rowVersion: null,
            documentUuid: document.uuid,
            documentSource,
            documentType: null,
            variant: null,
            fileName: document.fileName,
            url: null,
            uploadedAt: null,
          })),
      ]);
      return;
    }

    const errors = (await Promise.all(documents.map((document) => linkDocument(objectUuid, document.uuid)))).filter(
      (error): error is ServiceError => error !== undefined
    );

    if (errors.length > 0) {
      Toast.error({ message: `The document was uploaded, but could not be attached: ${errors[0].message}` });
    }

    /*
      The links stand — they were written against the object the upload was started from — but that object is
      no longer the one on show, and its reload would be the newest read, so the list would become its
      documents.
    */
    if (!isCurrentPanelSession()) return;

    // Reloaded either way: a partial failure still leaves the successful links to show.
    await fetchAttachments();
  };

  const handleRemoveAttachment = async (documentUuid: string): Promise<void> => {
    const attachment = attachments.find((a) => a.documentUuid === documentUuid);
    if (!attachment) return;

    // Staged, so there is no link to delete — dropping it locally is enough.
    if (!attachment.uuid || attachment.rowVersion === null) {
      setAttachments((prev) => prev.filter((a) => a.documentUuid !== documentUuid));
      return;
    }

    const lifecycle = lifecycleRef.current;

    const res = await deleteObjectDocument({
      uuid: attachment.uuid,
      objectUuid: null,
      objectType: null,
      documentUuid: null,
      rowVersion: attachment.rowVersion,
    });

    if (res.status === Status.Error) {
      Toast.error({ message: res.errors?.[0]?.message ?? "This attachment could not be removed" });
      return;
    }

    /*
      As above: the unlink stands, but an object the panel has left behind may not reload over the one on show —
      and confirming a removal the user can no longer see says it of whatever is now open. A failure is worth
      reporting whichever object it belonged to, so only the confirmation waits on the panel still being there.
    */
    if (lifecycle !== lifecycleRef.current) return;

    Toast.success({ message: "Attachment removed successfully" });
    await fetchAttachments();
  };

  /*
    The download endpoints identify a document by its latest *version*, but an object_document link records
    only the document. The version is read back off the parent's document search — the same read the document
    tables download from — rather than being carried on the link.
  */
  const resolveLatestVersionUuid = async (documentUuid: string): Promise<string | undefined> => {
    if (!parentUuid) return undefined;

    const filter = { results: { uuid: { operator: "eq" as const, value: documentUuid } } };
    const paging = { afterCursor: null, beforeCursor: null, limit: 1 };

    const res =
      documentSource === ObjectDocumentSource.DEAL_DOCUMENT
        ? await searchDealDocuments({ dealUuid: parentUuid, paging, filter })
        : // A landholding's attachments are project documents held against the group, so they are searched by it.
          await searchProjectDocuments({
            projectUuid: documentScope === "project" ? parentUuid : null,
            groupUuid: documentScope === "group" ? parentUuid : null,
            paging,
            filter,
          });

    if (res.status !== Status.Success) return undefined;

    return res.data?.results?.[0]?.versionUuid;
  };

  const handleDownloadAttachment = async (documentUuid: string): Promise<void> => {
    const versionUuid = await resolveLatestVersionUuid(documentUuid);

    /*
      Without a version there is no download to make. Reported rather than falling back to the link's file url:
      that url is not readable for a private document, so it would look like a download that produced nothing.
    */
    if (!versionUuid) {
      Toast.error({ message: "This attachment could not be downloaded" });
      return;
    }

    const res =
      documentSource === ObjectDocumentSource.DEAL_DOCUMENT
        ? await downloadDealDocument({ dealDocumentHistoryUuid: versionUuid })
        : await downloadProjectDocument({ projectDocumentHistoryUuid: versionUuid });

    // Reports its own failure, and turns the response into the browser's download.
    initiateDocumentDownloadViaBrowser(res);
  };

  const saveAttachments = async (savedObjectUuid: string): Promise<ServiceError[] | undefined> => {
    const staged = attachments.filter((attachment) => attachment.uuid === null);
    if (staged.length === 0) return undefined;

    const errors = (
      await Promise.all(staged.map((attachment) => linkDocument(savedObjectUuid, attachment.documentUuid)))
    ).filter((error): error is ServiceError => error !== undefined);

    return errors.length > 0 ? errors : undefined;
  };

  /*
    Invalidates any load — and any upload or removal whose reload has not run yet — as well as clearing the
    list: either arriving after the panel closed would otherwise repopulate it against the object just
    dismissed.
  */
  const resetAttachments = (): void => {
    requestIdRef.current += 1;
    lifecycleRef.current += 1;
    setAttachments([]);
    setIsAttachmentsLoading(false);
  };

  // Read at the point an upload flow opens, so the session it belongs to is fixed before the file transfers.
  const beginUpload = (): number => lifecycleRef.current;

  return {
    attachments,
    isAttachmentsLoading,
    beginUpload,
    resetAttachments,
    handleDocumentsUploaded,
    handleRemoveAttachment,
    handleDownloadAttachment,
    saveAttachments,
  };
};
